import { INIT_PRODUCTS } from "../../constants/products";
import { migrateProduct } from "../../utils/productMigration";
import { buildDefaultCoachCharges } from "../../constants/coachCharges";
import { sanitizeText } from "../../utils/sanitize";
import { TRIAL_DURATION_DAYS } from "../../config/plans";
import { shouldWriteRemote, upsertProductApp } from "../../services/supabaseEntityWrites";
import { logger, toLogError } from "../../utils/logger";

function newProductId() {
  return crypto.randomUUID();
}

/**
 * Produits / offres + plan d'abonnement app (trial).
 */
export function createBillingSlice(set, get) {
  return {
    products: INIT_PRODUCTS.map(migrateProduct),
    coachCharges: [],
    plan: "free",
    trialEnd: null,

    setCoachCharges: (fn) =>
      set((s) => ({
        coachCharges: typeof fn === "function" ? fn(s.coachCharges || []) : fn,
      })),

    seedCoachChargesIfEmpty: () => {
      const cur = get().coachCharges || [];
      if (cur.length > 0) return;
      set({ coachCharges: buildDefaultCoachCharges() });
    },

    addCoachCharge: (partial) => {
      const row = {
        id: crypto.randomUUID(),
        name: sanitizeText(partial.name || "Charge", 120),
        type: ["proportional", "fixed_monthly", "fixed_annual", "variable"].includes(partial.type)
          ? partial.type
          : "fixed_monthly",
        percentage: partial.percentage != null ? Math.min(100, Math.max(0, Number(partial.percentage))) : null,
        amount: partial.amount != null ? Math.max(0, Number(partial.amount)) : null,
        category: partial.category || "other",
        isActive: partial.isActive !== false,
        notes: sanitizeText(partial.notes || "", 500),
      };
      set((s) => ({ coachCharges: [...(s.coachCharges || []), row] }));
      return row;
    },

    updateCoachCharge: (id, patch) =>
      set((s) => ({
        coachCharges: (s.coachCharges || []).map((c) =>
          c.id === id
            ? {
                ...c,
                ...patch,
                name: patch.name != null ? sanitizeText(patch.name, 120) : c.name,
                notes: patch.notes != null ? sanitizeText(patch.notes, 500) : c.notes,
              }
            : c,
        ),
      })),

    deleteCoachCharge: (id) =>
      set((s) => ({ coachCharges: (s.coachCharges || []).filter((c) => c.id !== id) })),

    setProducts: (fn) =>
      set((s) => ({
        products: typeof fn === "function" ? fn(s.products) : fn,
      })),

    createProduct: (partial) => {
      const name = sanitizeText(partial.name || "Nouvelle offre", 200);
      const rawType = partial.type === "program" ? "programme" : partial.type;
      const type = ["session", "pack", "subscription", "programme"].includes(rawType)
        ? rawType
        : "session";
      const amount = Math.max(0, Number(partial.pricing?.amount) || 0);
      const count =
        partial.sessions?.count != null
          ? Math.max(0, Number(partial.sessions.count))
          : type === "pack"
            ? 10
            : type === "subscription"
              ? 4
              : type === "programme"
                ? 12
                : 1;
      const validityDays =
        partial.sessions?.validityDays != null
          ? Math.max(0, Number(partial.sessions.validityDays))
          : type === "pack"
            ? 90
            : null;

      const durationWeeks =
        type === "programme" && partial.durationWeeks != null ? Math.max(1, Number(partial.durationWeeks)) : null;

      const sessionsBase = {
        count: type === "session" ? 1 : count,
        validityDays,
      };
      if (type === "pack") {
        sessionsBase.packValidityStart =
          partial.sessions?.packValidityStart === "purchase" ? "purchase" : "first_session";
      }

      const product = {
        id: newProductId(),
        name,
        type,
        pricing: {
          amount,
          perSession:
            (type === "pack" || type === "programme") && count > 0
              ? Math.round((amount / count) * 100) / 100
              : null,
          recurrence: type === "subscription" ? "monthly" : null,
        },
        sessions: sessionsBase,
        subscription:
          type === "subscription"
            ? {
                maintainByDefaultOnStandby: partial.subscription?.maintainByDefaultOnStandby === true,
                standbyBillingMode:
                  partial.subscription?.standbyBillingMode === "prorata_days" ? "prorata_days" : "full_month",
              }
            : undefined,
        programmeObjective:
          type === "programme" ? sanitizeText(partial.programmeObjective || partial.description || "", 500) : undefined,
        durationWeeks,
        includesAssessment: type === "programme" ? !!partial.includesAssessment : undefined,
        paymentSchedule: type === "programme" ? partial.paymentSchedule || "once" : undefined,
        deliveryMode: partial.deliveryMode || "in_person",
        description: sanitizeText(partial.description || "", 500),
        isActive: true,
        createdAt: new Date().toISOString().slice(0, 10),
      };

      set((s) => ({ products: [...s.products, product] }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const r = await upsertProductApp(product, get().coachId);
        if (!r.ok) {
          set((s) => ({ products: s.products.filter((p) => p.id !== product.id) }));
          logger.error("[createProduct] remote write failed", toLogError(r.error), { productId: product.id });
        }
      })();

      return product;
    },

    updateProduct: (id, patch) => {
      const prev = get().products;
      set((s) => ({
        products: s.products.map((p) => {
          if (p.id !== id) return p;
          const next = { ...p, ...patch };
          if (patch.name != null) next.name = sanitizeText(patch.name, 200);
          if (patch.description != null) next.description = sanitizeText(patch.description, 500);
          if (patch.isActive === false) next.isActive = false;
          if (patch.sessions) {
            next.sessions = { ...p.sessions, ...patch.sessions };
          }
          if (patch.subscription) {
            next.subscription = { ...(p.subscription || {}), ...patch.subscription };
          }
          if (patch.pricing) {
            next.pricing = { ...p.pricing, ...patch.pricing };
          }
          const cnt = next.sessions?.count;
          if (
            (next.type === "pack" || next.type === "programme") &&
            cnt > 0 &&
            next.pricing?.amount != null
          ) {
            next.pricing = {
              ...next.pricing,
              perSession: Math.round((Number(next.pricing.amount) / cnt) * 100) / 100,
            };
          }
          return next;
        }),
      }));

      void (async () => {
        if (!shouldWriteRemote(get().coachId)) {
          get().persist?.();
          return;
        }
        const p = get().products.find((x) => x.id === id);
        const r = await upsertProductApp(p, get().coachId);
        if (!r.ok) {
          set({ products: prev });
          logger.error("[updateProduct] remote write failed", toLogError(r.error), { productId: id });
        }
      })();
    },

    deactivateProduct: (id) => {
      get().updateProduct(id, { isActive: false });
    },

    setPlan: (v) => set({ plan: v }),
    startTrial: () => {
      const end = new Date();
      end.setDate(end.getDate() + TRIAL_DURATION_DAYS);
      set({ plan: "trial", trialEnd: end.toISOString() });
    },
    expireTrial: () => set({ plan: "free", trialEnd: null }),
  };
}
